// Native in-app OAuth for Capacitor (iOS / Android).
//
// Uses the OS-native sign-in sheets — no Safari, no SFSafariViewController.
//  • Apple  → AuthenticationServices (system sheet) via @capacitor-community/apple-sign-in
//
// The returned identity token is exchanged with Supabase via
// `supabase.auth.signInWithIdToken`, so the session is created directly inside
// the WebView with zero redirect round-trip.
//
// Native setup (one-time, on the Mac):
//   iOS Apple:
//     • Xcode → Signing & Capabilities → + Capability → "Sign In with Apple"

import { supabase } from "@/integrations/supabase/client";

export const WEB_AUTH_ORIGIN = "https://greentrack.life";
export const NATIVE_REDIRECT_URI = "app.greentrack.life://oauth-callback";

type OAuthProvider = "apple";

export function isNative(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean(
    (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } })
      .Capacitor?.isNativePlatform?.(),
  );
}

/** Web-only redirect target. Native flows don't need this. */
export function getOAuthRedirectUri(webPath: string = "/app"): string {
  if (typeof window === "undefined") return webPath;
  return window.location.origin + webPath;
}

export function getPasswordResetRedirectUri(): string {
  if (isNative()) return `${WEB_AUTH_ORIGIN}/callback?mode=recovery`;
  if (typeof window === "undefined") return "/reset-password";
  return `${window.location.origin}/reset-password`;
}

function randomNonce(len = 32): string {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, "0")).join("");
}

async function nativeApple(): Promise<{ error: Error | null }> {
  try {
    const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
    const rawNonce = randomNonce();
    const hashedNonce = await sha256Hex(rawNonce);
    const res = await SignInWithApple.authorize({
      clientId: "app.greentrack.life",
      redirectURI: `${WEB_AUTH_ORIGIN}/callback`,
      scopes: "email name",
      state: randomNonce(16),
      nonce: hashedNonce,
    });
    const idToken = res.response?.identityToken;
    if (!idToken) return { error: new Error("Apple did not return an identity token.") };
    const { error } = await supabase.auth.signInWithIdToken({
      provider: "apple",
      token: idToken,
      nonce: rawNonce,
    });
    return { error: error ?? null };
  } catch (e) {
    return { error: e instanceof Error ? e : new Error(String(e)) };
  }
}

export async function startNativeOAuth(
  provider: OAuthProvider,
): Promise<{ error: Error | null; redirected: boolean }> {
  if (!isNative()) {
    return { error: new Error("Native OAuth can only run inside the app."), redirected: false };
  }
  const { error } = await nativeApple();
  return { error, redirected: false };
}

let listenerRegistered = false;

/**
 * Register a Capacitor App URL listener so password-reset deep links open the
 * in-app reset page. OAuth no longer uses deep links (native SDKs return
 * inline), so this is now recovery-only.
 */
export async function registerAuthDeepLinkHandler(): Promise<void> {
  if (!isNative() || listenerRegistered) return;
  listenerRegistered = true;
  try {
    const { App } = await import("@capacitor/app");
    await App.addListener("appUrlOpen", ({ url }) => {
      try {
        if (!url.includes("reset-password") && !url.includes("type=recovery")) return;
        const [beforeHash, rawHash = ""] = url.split("#");
        const search = beforeHash.includes("?") ? beforeHash.slice(beforeHash.indexOf("?") + 1) : "";
        window.location.assign(`/reset-password#${rawHash || search}`);
      } catch (e) {
        console.warn("[nativeAuth] deep-link handler failed", e);
      }
    });
  } catch (e) {
    console.warn("[nativeAuth] @capacitor/app unavailable", e);
  }
}
